import React from 'react';
import { Dumbbell, Mail, Phone, MapPin, Clock } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { mockData } from '../mock';

export const Footer = () => {
  const { business } = mockData;
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const navigateToPage = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="container mx-auto px-4 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="/images/logo.png"
                alt="MSK and Sports Physio Logo"
                width="48"
                height="48"
                loading="lazy"
                className="h-12 w-12 object-contain"
              />
              <div className="flex flex-col items-start">
                <span className="text-lg font-bold text-white leading-tight">MSK and Sports Physio - Param Jumani</span>
              </div>
            </div>
            <p className="text-sm leading-relaxed mb-6">
              Expert physiotherapy care for athletes and active individuals. Get back to peak performance with our specialized treatment.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-6">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => scrollToSection('services')}
                  className="text-gray-400 hover:text-[#2563eb] transition-colors duration-200 text-sm"
                >
                  Services
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('team')}
                  className="text-gray-400 hover:text-[#2563eb] transition-colors duration-200 text-sm"
                >
                  Team
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('testimonials')}
                  className="text-gray-400 hover:text-[#2563eb] transition-colors duration-200 text-sm"
                >
                  Testimonials
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigateToPage('/pricing')}
                  className="text-gray-400 hover:text-[#2563eb] transition-colors duration-200 text-sm"
                >
                  Pricing
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigateToPage('/contact')}
                  className="text-gray-400 hover:text-[#2563eb] transition-colors duration-200 text-sm"
                >
                  Contact
                </button>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-6">Our Services</h3>
            <ul className="space-y-3">
              <li className="text-gray-400 text-sm">Sports Physiotherapy</li>
              <li className="text-gray-400 text-sm">Paediatric Physiotherapy</li>
              <li className="text-gray-400 text-sm">Post Operative Care</li>
              <li className="text-gray-400 text-sm">Manual Therapy</li>
              <li className="text-gray-400 text-sm">Exercise Rehabilitation</li>
              <li className="text-gray-400 text-sm">Taping</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-6">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-[#2563eb] mt-0.5 flex-shrink-0" />
                <a href={`mailto:${business.email}`} className="text-sm text-gray-400 hover:text-[#06b6d4] transition-colors duration-200">
                  {business.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-[#2563eb] mt-0.5 flex-shrink-0" />
                <a href={`tel:${business.phone}`} className="text-sm text-gray-400 hover:text-[#06b6d4] transition-colors duration-200">
                  {business.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#2563eb] mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-400">{business.address}</span>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-[#2563eb] mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-400">{business.hours}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Areas We Cover Section */}
        <div className="mt-12 pt-8 border-t border-gray-800">
          <div className="text-center">
            <h3 className="text-white font-semibold text-lg mb-4">Areas We Cover</h3>
            <p className="text-sm text-gray-400 leading-relaxed">
              Harrow • Harrow on the Hill • North Harrow • South Harrow • Wealdstone • Kenton • Pinner • Stanmore • Wembley • Northwood
            </p>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} MSK and Sports Physio. All rights reserved.
            </p>
            <div className="flex gap-6">
              <button 
                onClick={() => navigateToPage('/privacy-policy')}
                className="text-sm text-gray-500 hover:text-[#2563eb] transition-colors duration-200"
              >
                Privacy Policy
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
